/**
 * Canonical eligibility *display* status — the single enum every status surface
 * reads from (Daily Cockpit rows + glance rollup, Recent Checks, failure
 * slideout). Two levels, per the cockpit redesign:
 *
 *   - the detailed status (`EligibilityDisplayStatus`) — the row-level label
 *     staff read, with an icon + reason tooltip;
 *   - the rollup group (`RollupGroup`) — the at-a-glance color the cockpit
 *     summarizes a day's health under (🟢 resolved / 🟡 attention / 🔴 broken /
 *     ⚪ neutral).
 *
 * Color for the glance, detail on the row.
 *
 * The enum literals match what's persisted on appointment docs
 * (`eligibilityStatus: 'data_issue' | 'stale' | … | null`) and what
 * `checkStatusToCockpit` returns, so this is a pure read-time classification —
 * no Firestore migration.
 *
 * `ROLLUP_OF` is the one place color placement lives: a product decision (e.g.
 * "Ineligible needs the front desk's attention, not a green light") flips a
 * single entry here and every surface follows. Framework-free on purpose so it
 * can run on either side.
 */

import { MAX_RETRIES, STEDI_MAX_RETRIES } from './retryPolicy.js'

export type EligibilityDisplayStatus =
  | 'eligible'
  | 'not_eligible' // Ineligible — payer answered, coverage inactive / not enrolled
  | 'needs_review' // human action on data we have (#37)
  | 'data_issue'   // "Cannot Check" — missing fields / unresolvable payer, never sent
  | 'error'        // system / payer-service failure (#37)
  | 'stale'        // a definitive answer whose visit has passed — re-verify (#83)
  | 'in_progress'  // check is running (pending / retrying)
  | 'not_checked'  // no check yet

export type RollupGroup = 'resolved' | 'attention' | 'broken' | 'neutral'

/**
 * The single grouping map — the "configurable per product decision" switch.
 *   🟢 resolved  — we have a definitive answer; no action needed.
 *   🟡 attention — we have info but a human must act (review / fix data / re-verify).
 *   🔴 broken    — a system or payer-service failure.
 *   ⚪ neutral   — no current answer; the system is (or will be) working on it.
 */
export const ROLLUP_OF: Record<EligibilityDisplayStatus, RollupGroup> = {
  eligible: 'resolved',
  // Definitive "no" — the check is done, but the front desk's work begins
  // (collect self-pay / fix a stale card / contact). At the glance it must read
  // as "act before the visit", not a green "nothing to do". Detail (the
  // "Ineligible" ✕ label) lives on the row; the glance only owns the color.
  not_eligible: 'attention',
  needs_review: 'attention',
  data_issue: 'attention',
  stale: 'attention', // decayed answer — staff should re-verify before the visit
  error: 'broken',
  in_progress: 'neutral',
  not_checked: 'neutral',
}

/** Rollup group for a status; `null`/`undefined` (no status yet) reads neutral. */
export function rollupOf(status: EligibilityDisplayStatus | null | undefined): RollupGroup {
  return status ? ROLLUP_OF[status] : 'neutral'
}

export type StatusIconKey = 'check' | 'x' | 'alert' | 'refresh' | 'clock' | 'spinner'

/**
 * Row-level presentation per detailed status: the label + which icon to show.
 * `iconKey` is mapped to an icon component on the frontend (this module stays
 * framework-free).
 *
 * Chip COLOR is deliberately NOT here — it comes from the status's rollup group
 * (`ROLLUP_OF` → `cockpit-status--{group}` / `recent-status--{group}`), so a row
 * chip always matches the glance and there is a single place (ROLLUP_OF) to
 * change color placement.
 */
export interface DisplayStatusMeta {
  label: string
  iconKey: StatusIconKey
  /** Optional secondary line under the pill. Only the auto-retry meta sets it
   *  (the next-attempt time, #289) — the fixed status metas below have nothing
   *  to add, so every surface renders it conditionally. */
  sublabel?: string
}

export const DISPLAY_STATUS_META: Record<EligibilityDisplayStatus, DisplayStatusMeta> = {
  eligible: { label: 'Eligible', iconKey: 'check' },
  not_eligible: { label: 'Ineligible', iconKey: 'x' },
  needs_review: { label: 'Needs review', iconKey: 'alert' },
  data_issue: { label: 'Cannot check', iconKey: 'alert' },
  stale: { label: 'Stale', iconKey: 'clock' },
  error: { label: 'Error', iconKey: 'refresh' },
  in_progress: { label: 'Check in progress', iconKey: 'spinner' },
  not_checked: { label: 'Not checked', iconKey: 'clock' },
}

/** Glance-level presentation per rollup group. `tone` is the css modifier
 *  suffix for the horizon-card bar segment + legend dot. */
export interface RollupGroupMeta {
  label: string
  tone: string
}

export const ROLLUP_GROUP_META: Record<RollupGroup, RollupGroupMeta> = {
  resolved: { label: 'Resolved', tone: 'resolved' },
  attention: { label: 'Needs attention', tone: 'attention' },
  broken: { label: 'Broken', tone: 'broken' },
  neutral: { label: 'Pending', tone: 'neutral' },
}

/** Left-to-right order for the rollup bar segments and legend. */
export const ROLLUP_ORDER: RollupGroup[] = ['resolved', 'attention', 'broken', 'neutral']

// ── Auto-retry display state ──────────────────────────────────────────
// A check that's actively scheduled for automatic retry (transient payer AAA
// 42/79/80, network, or Stedi 5xx) is "the system is working on it", NOT a
// human-action state. Every status surface (Cockpit, Recent Checks, failure
// slideout) routes through these helpers so they all agree.

/** Minimal check shape these helpers read — decoupled from EligibilityCheckDoc
 *  so this module stays framework- and import-light. */
export interface AutoRetryCheckFields {
  recoveryAction?: string
  status?: string
  type?: string
  retryCount?: number
  /** ISO stamp of the earliest next attempt, written by the sweep's backoff
   *  (`sweepStediRetries`). See `nextAttemptDue` for what its absence means. */
  nextRetryAt?: string | null
}

/** Per-route retry budget: Stedi gets the longer transient-payer budget; RPA
 *  (VSP/EyeMed and any non-stedi route) uses the shorter one. */
function retryBudget(type: string | undefined): number {
  return type && type !== 'stedi' ? MAX_RETRIES : STEDI_MAX_RETRIES
}

/**
 * True while a failed check is still scheduled for automatic retry (within its
 * route's budget). Gated on a failed status so a (defensively) stale
 * recoveryAction on a success can't read as retrying.
 */
export function isAutoRetrying(check: AutoRetryCheckFields | null | undefined): boolean {
  if (!check || check.recoveryAction !== 'auto_retry') return false
  if (check.status !== 'error' && check.status !== 'timeout') return false
  return (check.retryCount ?? 0) < retryBudget(check.type)
}

/** Total attempts (initial + retries) for "Attempt X of Y" displays, by route. */
export function maxTotalAttempts(check: { type?: string }): number {
  return retryBudget(check.type) + 1
}

/** The one retry-progress phrase every surface shares — the same attempts
 *  numbering as the failure slideout's "Attempt {retryCount + 1} of
 *  {maxTotalAttempts}" counter, so the row pill, its tooltip, and the slideout
 *  a click away can never show different numbers for the same check. */
export function retryAttemptProgress(check: AutoRetryCheckFields): string {
  return `attempt ${(check.retryCount ?? 0) + 1} of ${maxTotalAttempts(check)}`
}

/** The retry sweeps run every 15 minutes, so a check that is due carries at
 *  most one sweep interval of waiting before its next attempt. */
const RETRY_SWEEP_INTERVAL_MIN = 15

/** Clock time for the next-attempt phrase. Same calendar day → time only (the
 *  common case: the longest Stedi backoff is 4h); a stamp that crosses midnight
 *  keeps its date so "1:05 AM" can't read as "five minutes ago". Browser-local,
 *  matching the Date column both tables already render. */
function formatNextAttemptClock(at: Date, now: Date): string {
  const sameDay = at.toDateString() === now.toDateString()
  try {
    return at.toLocaleString('en-US', {
      ...(sameDay ? {} : { month: 'short', day: 'numeric' }),
      hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return at.toISOString()
  }
}

/**
 * When the next automatic attempt is due (#289) — the half of the retry state
 * that answers the question the ticket opens with: at 9:48 PM, wait or act?
 *
 * `nextRetryAt` is the sweep's backoff stamp and exists ONLY on a Stedi check
 * that has already been retried at least once (15m→30m→1h→2h→4h). Its absence
 * is not unknown-ness — a first failure, and every RPA check (whose retry sweep
 * carries no backoff at all), are simply due on the next sweep pass, which is
 * also what an elapsed stamp means. So there are exactly two honest answers,
 * a clock time and "within 15 min", and never "unknown".
 */
export function nextAttemptDue(check: AutoRetryCheckFields, now: Date = new Date()): string {
  const at = check.nextRetryAt ? new Date(check.nextRetryAt) : null
  if (!at || Number.isNaN(at.getTime()) || at.getTime() <= now.getTime()) {
    return `next attempt within ${RETRY_SWEEP_INTERVAL_MIN} min`
  }
  return `next attempt ~${formatNextAttemptClock(at, now)}`
}

/**
 * Row-pill presentation for an auto-retrying check (#289). A failed check
 * waiting on a backoff must never wear the same "Check in progress" label a
 * genuinely-running check gets — from the table alone, staff could not tell
 * "the system is working on this right now" from "this errored hours ago and
 * won't be retried until later". The label says failed-and-waiting outright
 * (with the check's true position in its attempt budget once a retry has
 * run), and the clock icon replaces the in-progress spinner. The chip keeps
 * the neutral rollup tone: "no current answer, the system will work on it"
 * stays true — only the claim of live work goes away.
 *
 * `sublabel` carries the second half of the ticket's AC — when the next
 * attempt is due — so a surface that renders the label gets the timing from
 * the same call instead of deciding it locally.
 */
export function retryingStatusMeta(check: AutoRetryCheckFields, now: Date = new Date()): DisplayStatusMeta {
  const n = check.retryCount ?? 0
  return {
    label: n > 0 ? `Failed — ${retryAttemptProgress(check)}` : 'Failed — will retry',
    iconKey: 'clock',
    sublabel: nextAttemptDue(check, now),
  }
}

/** Badge/tooltip text explaining why an auto-retrying check is waiting, so staff
 *  know no action is needed (vs. the "needs review" it would otherwise read as). */
export function retryingReason(check: AutoRetryCheckFields, now: Date = new Date()): string {
  const n = check.retryCount ?? 0
  const progress = n > 0 ? ` (${retryAttemptProgress(check)})` : ''
  const due = nextAttemptDue(check, now)
  return `Payer temporarily unavailable — automatically retrying, no action needed${progress}. ` +
    `${due.charAt(0).toUpperCase()}${due.slice(1)}.`
}
